import {
  ContentSourceType,
  ContentType,
  PublicationContentType
} from '@app/entities/enums/multimedia/content-status-types.enum';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { Pagination } from './pagination.model';

export interface AbstractMultimediaItemResponse {
  item: AbstractMultimediaItem;
}

export abstract class AbstractMultimediaItem {
  contentType: PublicationContentType | ContentType;
  id: number;
  publicationDate: string;
  summary: string;
  text: string;
  title: string;
  internalTitle: string;
  url?: string;
  views?: number;
  comments?: number | string;
  contentSource?: ContentSourceType;
  language?: string;
  publishAvailable?: boolean;
  shareable?: boolean;
  thumbnail?: string;
  slug?: string;
  commentsAllowed?: boolean;
  link?: string;
  public?: boolean;
  numComments?: number;
  userComments?: number;
  returned: boolean;
  statusInfo: string;
  internalLink?: string;
  linksOnWebview?: boolean;
  videoProvider?: string;
  multiple?: MSafeAny[];
  label?: { description: string; image: string };
  audience?: { id: string; description: string };
  description?: string;
  publicationHour?: string;
  translationKey: string;

  constructor(data: MSafeAny) {
    this.contentType = data.content_type;
    this.id = data.id;
    this.publicationDate = data.publication_date;
    this.summary = data.summary;
    this.text = data.text;
    this.title = data.title;
    this.internalTitle = data.internal_title;
    this.url = data.url;
    this.views = data.views;
    this.comments = data.comments;
    this.contentSource = data.content_source;
    this.language = data.language;
    this.publishAvailable = data.publish_available !== false;
    this.shareable = data.shareable;
    this.thumbnail = data.thumbnail;
    this.slug = data.slug;
    this.commentsAllowed = data.comments_allowed;
    this.public = data.public;
    this.numComments = data.n_comments;
    this.userComments = data.user_comments;
    this.returned = data.returned;
    this.statusInfo = data.status_info;
    this.internalLink = data.internal_link;
    this.linksOnWebview = data.links_on_webview;
    this.multiple = data.multiple;
    this.label = data.label;
    this.translationKey = data.translation_key;

    this.audience = data.audience;
    this.description = data.description;
    this.publicationHour = data.publication_hour;
  }

  isOfficialContent(): boolean {
    return this.contentSource === ContentSourceType.PUBLICATION;
  }

  isPublic() {
    return this.contentSource === ContentSourceType.PUBLICATION && this.public;
  }

  isVideo(): boolean {
    return this.isOfficialContent()
      ? this.contentType === PublicationContentType.VIDEO
      : this.contentType === ContentType.VIDEO;
  }

  isMultimedia(): boolean {
    return this.isOfficialContent() ? this.contentType === PublicationContentType.MULTIMEDIA : false;
  }

  typeMultimediaCard(): string {
    return this.isMultimedia() && this.multiple && this.multiple.length > 0 ? this.multiple[0].type : '';
  }

  isMultimediaCardVideo(): boolean {
    return this.typeMultimediaCard() === PublicationContentType.VIDEO;
  }

  isMultimediaCardImage(): boolean {
    return this.typeMultimediaCard() === PublicationContentType.IMAGE;
  }

  isText(): boolean {
    return this.isOfficialContent()
      ? this.contentType === PublicationContentType.TEXT
      : this.contentType === ContentType.TEXT;
  }

  isImage(): boolean {
    return this.isOfficialContent()
      ? this.contentType === PublicationContentType.IMAGE
      : this.contentType === ContentType.IMAGE;
  }

  isNews(): boolean {
    return this.contentType === PublicationContentType.NEWS;
  }

  isPublishDisabled() {
    return this.publishAvailable === false;
  }

  isShareable() {
    return this.shareable;
  }

  isInternal(): boolean {
    return this.contentType === PublicationContentType.INTERNAL;
  }

  canShowComments(): boolean {
    return this.commentsAllowed || (this.numComments as number) > 0;
  }

  hasComments(): boolean {
    return (this.numComments as number) > 0;
  }

  hasCommentsAllowed(): boolean {
    return this.commentsAllowed as boolean;
  }

  getAnalyticsLabel(): string {
    return this.slug || '';
  }
}

export class AbstractPaginatedMultimediaItemResponse<T extends AbstractMultimediaItem = AbstractMultimediaItem> {
  items: T[];
  lastViewedId: number;
  pagination: Pagination;

  constructor(multimediaItemList: T[], pagination: Pagination, lastViewedId: number) {
    this.items = multimediaItemList;
    this.lastViewedId = lastViewedId;
    this.pagination = pagination;
  }
}
